import { useRef, useState } from 'react'

import { Box, Icon, Table } from '@factorialco/deprecated-design-system'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { NestedRows } from 'modules/finance/components/FinancialWorkspace/components/Table/table/NestedRows'
import { InsightTableGroupBy } from 'modules/finance/components/FinancialWorkspace/types/table'

import { defaultItems } from './defaults'

import './index.styles.css'

interface Props {
  title: string
  titleIcon?: React.ComponentProps<typeof Icon.Medium>['icon']
  rows: number
  columns: number
  children: React.ReactNode
  minRange?: number
  maxRange?: number
  tableHeadRef?: React.RefObject<HTMLTableSectionElement>
  level?: number
  dataTestId?: string
  expandedByDefault?: boolean
}

export const BlurRow = ({
  title,
  rows,
  columns,
  titleIcon,
  minRange,
  maxRange,
  children,
  tableHeadRef,
  level,
  dataTestId,
  expandedByDefault = false,
}: Props) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const [visibleWidth, setVisibleWidth] = useState(0)
  const [expanded, setExpanded] = useState(expandedByDefault)

  const { hasComparisonDate } = useFinancialWorkspace()

  const coloumnsCount = columns * (hasComparisonDate ? 3 : 1)

  const items = useUnsafeMemo(
    () => defaultItems({ title, titleIcon, rows, columns: columns, minRange, maxRange }),
    [title, titleIcon, rows, columns, minRange, maxRange]
  )

  const assignColumnWidths = useUnsafeCallback(() => {
    const tableColumns = tableRef.current?.querySelectorAll('td')
    const headerRows = tableHeadRef?.current?.querySelectorAll('tr')
    const headerRow = headerRows?.[headerRows.length - 1]
    const tableHeadColumns = headerRow?.querySelectorAll('th')

    if (tableColumns) {
      Array.from(tableColumns).forEach((col, index) => {
        const tableHead = tableHeadColumns?.[index]
        const width = tableHead?.offsetWidth

        if (width) {
          col.setAttribute(
            'style',
            `${col.getAttribute('style')}; width: ${width}px; max-width: ${width}px;`
          )
        }
      })
    }
  }, [tableRef, tableHeadRef])

  const buildObserver = () => {
    return new IntersectionObserver(
      ([entry]) => {
        setVisibleWidth(entry?.intersectionRect.width || 0)
      },
      {
        root: null,
        threshold: [0, 1],
      }
    )
  }

  useUnsafeEffect(() => {
    assignColumnWidths()
  }, [tableRef, tableHeadRef, assignColumnWidths, expanded])

  useUnsafeEffect(() => {
    const el = tableRef.current
    const headerEl = tableHeadRef?.current

    if (!el) return

    const updateWidth = () => {
      observerRef.current?.disconnect()
      const newObserver = buildObserver()
      newObserver.observe(el)
      observerRef.current = newObserver
    }

    const resizeObserver = new ResizeObserver(updateWidth)
    const headerResizeObserver = new ResizeObserver(() => {
      updateWidth()
      assignColumnWidths()
    })

    resizeObserver.observe(el)

    if (headerEl) {
      headerResizeObserver.observe(headerEl)
    }

    const onResize = () => {
      updateWidth()
      assignColumnWidths()
    }

    window.addEventListener('resize', onResize)

    updateWidth()

    return () => {
      resizeObserver.disconnect()
      headerResizeObserver.disconnect()
      observerRef.current?.disconnect()
      window.removeEventListener('resize', onResize)
    }
  }, [assignColumnWidths])

  return (
    <Table.Row>
      <Table.Cell colSpan={coloumnsCount + 1} padding="s0">
        <table
          ref={tableRef}
          data-testid={dataTestId}
          style={{
            width: '100%',
            borderSpacing: '0px',
            position: 'relative',
            tableLayout: 'fixed',
            borderCollapse: 'separate',
          }}
          className={'BlurRow_tableWithBlur'}
        >
          <NestedRows
            items={items}
            expanded={expandedByDefault}
            level={level ?? 0}
            currentPageCount={1}
            headerCount={columns}
            onExpand={setExpanded}
            groupBy={InsightTableGroupBy.Total}
          />

          {!!visibleWidth && expanded && (
            <Box
              style={{
                top: '0px',
                left: '0px',
                right: '0px',
                bottom: '0px',
                width: '100%',
                height: '100%',
                display: 'flex',
                position: 'absolute',
                justifyContent: 'center',
              }}
            >
              <Box
                ref={contentRef}
                position="sticky"
                alignItems="center"
                style={{ left: '0', zIndex: '1', width: `${visibleWidth}px` }}
              >
                {children}
              </Box>
            </Box>
          )}
        </table>
      </Table.Cell>
    </Table.Row>
  )
}
